import { Keyboard } from '@capacitor/keyboard';
import { estNatif } from './pont';

/**
 * Ce que le clavier prend à l'écran, et ce que l'application doit lui rendre.
 *
 * Le symptôme : sur téléphone, la barre des catégories — fixée en bas de l'écran — venait
 * se poser **par-dessus** le champ de saisie dès que le clavier s'ouvrait, dans le Journal
 * comme dans la Messagerie. Deux causes s'additionnaient, et corriger l'une sans l'autre
 * ne suffisait pas :
 *
 * 1. **L'activité ne déclarait pas comment réagir au clavier** (`windowSoftInputMode`
 *    absent du manifeste). Android choisissait alors de *pousser* la fenêtre vers le haut
 *    plutôt que de la redimensionner : la barre, ancrée au bas d'une fenêtre restée haute,
 *    montait avec elle et recouvrait la saisie. Le manifeste demande désormais
 *    `adjustResize`.
 * 2. **Depuis Android 15, `adjustResize` ne fait plus rien** : l'affichage est bord à bord
 *    par décret, la fenêtre n'est plus redimensionnée, et c'est à l'application de tenir
 *    compte de la place prise par le clavier. C'est ce que fait ce module.
 *
 * D'où une mesure plutôt qu'une hypothèse : on demande au système la hauteur du clavier,
 * on regarde ce que la fenêtre a **effectivement** cédé, et on ne compense que la
 * différence. Sur un téléphone qui redimensionne, elle vaut zéro et rien ne bouge ; sur un
 * téléphone qui ne redimensionne pas, elle vaut toute la hauteur du clavier. Le même code
 * sert les deux, sans avoir à deviner la version d'Android.
 */

/** L'attribut que la feuille de style observe. Posé sur `<html>`. */
const ATTRIBUT = 'clavier';

/** La hauteur que la mise en page doit rendre au clavier. */
const VARIABLE = '--hauteur-clavier';

/**
 * Ce que le clavier couvre **encore**, une fois la fenêtre redimensionnée.
 *
 * Pure et exportée pour être éprouvée seule : c'est le seul calcul du module, et le seul
 * endroit où une erreur produirait un décalage invisible en test de rendu.
 *
 * Jamais négatif : une fenêtre qui a cédé plus que la hauteur annoncée — barre de
 * navigation du système qui disparaît en même temps — ne doit pas faire remonter la mise
 * en page au-dessus du haut de l'écran.
 */
export function couvertureClavier(
  hauteurClavier: number,
  hauteurAuRepos: number,
  hauteurCourante: number,
): number {
  const cede = Math.max(0, hauteurAuRepos - hauteurCourante);
  return Math.max(0, Math.round(hauteurClavier - cede));
}

/**
 * Branche le clavier sur la mise en page. Rend la fonction de débranchement.
 *
 * Hors coque, ne fait rien : dans un navigateur, c'est la balise `viewport`
 * (`interactive-widget=resizes-content`, voir `index.html`) qui obtient le même résultat
 * sans une ligne de JavaScript.
 */
export async function brancherClavier(): Promise<() => Promise<void>> {
  if (!estNatif()) return () => Promise.resolve();

  const racine = document.documentElement;
  // La hauteur de référence : celle de la fenêtre clavier fermé. Relevée maintenant, puis
  // à chaque fermeture — une rotation d'écran la change, et une référence périmée ferait
  // compenser une hauteur qui n'est plus la bonne.
  let hauteurAuRepos = globalThis.innerHeight;

  const ouvrir = (couverture: number | null): void => {
    racine.dataset[ATTRIBUT] = 'ouvert';
    if (couverture !== null) racine.style.setProperty(VARIABLE, `${couverture}px`);
  };

  const fermer = (): void => {
    delete racine.dataset[ATTRIBUT];
    racine.style.removeProperty(VARIABLE);
  };

  const ecouteurs = await Promise.all([
    // `will` et non `did` pour l'ouverture : la barre du bas doit s'effacer **pendant**
    // que le clavier monte, pas après. La compensation, elle, attend la mesure.
    Keyboard.addListener('keyboardWillShow', () => ouvrir(null)),
    Keyboard.addListener('keyboardDidShow', ({ keyboardHeight }) =>
      ouvrir(couvertureClavier(keyboardHeight, hauteurAuRepos, globalThis.innerHeight)),
    ),
    Keyboard.addListener('keyboardWillHide', fermer),
    Keyboard.addListener('keyboardDidHide', () => {
      fermer();
      hauteurAuRepos = globalThis.innerHeight;
    }),
  ]);

  // La rotation de l'écran, clavier fermé : nouvelle hauteur de référence. Clavier ouvert,
  // on ne touche à rien — la fenêtre est justement en train de céder la place qu'on mesure.
  const surRedimensionnement = (): void => {
    if (racine.dataset[ATTRIBUT] === undefined) hauteurAuRepos = globalThis.innerHeight;
  };
  globalThis.addEventListener('resize', surRedimensionnement);

  return async () => {
    globalThis.removeEventListener('resize', surRedimensionnement);
    fermer();
    for (const ecouteur of ecouteurs) await ecouteur.remove();
  };
}
